/**
 * The only module that imports `posthog-js`.
 *
 * The import is dynamic and guarded by `isPostHogEnabled()`, which gives two
 * things: the SDK (a heavy dependency once session replay is on) stays off the
 * critical path, and with no project key configured it is never fetched at all.
 *
 * Every export is safe to call at any time. Calls made before the SDK finishes
 * loading are not dropped — they attach to the same load promise, and promise
 * callbacks run in registration order, so event ordering is preserved.
 */
import type { PostHog, Properties } from 'posthog-js';
import { buildInitOptions, getPostHogKey, isPostHogEnabled } from './config';
import { hasAnalyticsConsent } from './consent';
import type { PostHogEventMap, PostHogEventName } from './events';

let loadPromise: Promise<PostHog | null> | null = null;

/**
 * Load and initialise the SDK exactly once. Resolves to `null` — never rejects —
 * when PostHog is disabled, when running on the server, or when the chunk fails
 * to load (an ad blocker, a network hiccup). Analytics must never break the app.
 */
export function loadPostHog(): Promise<PostHog | null> {
  if (loadPromise) return loadPromise;

  if (typeof window === 'undefined' || !isPostHogEnabled()) {
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }

  loadPromise = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(getPostHogKey(), {
        ...buildInitOptions(),
        // Consent is resolved at load time rather than patched afterwards, so a
        // capture that races the provider still respects a withdrawn consent.
        opt_out_capturing_by_default: !hasAnalyticsConsent(),
      });
      return posthog;
    })
    .catch(() => null);

  return loadPromise;
}

/** Run `fn` against the SDK once it is ready. Never throws, never rejects. */
function withPostHog(fn: (posthog: PostHog) => void): void {
  void loadPostHog().then((posthog) => {
    if (!posthog) return;
    try {
      fn(posthog);
    } catch {
      // Analytics must never affect UX — swallow all errors.
    }
  });
}

/** Send a declared event. The name and its properties are checked against `events.ts`. */
export function capture<E extends PostHogEventName>(
  event: E,
  properties: PostHogEventMap[E],
): void {
  withPostHog((posthog) => posthog.capture(event, properties as Properties));
}

/** Send a `$pageview`. Separate from `capture()` — `$pageview` is PostHog's own event. */
export function capturePageview(properties?: Properties): void {
  withPostHog((posthog) => posthog.capture('$pageview', properties));
}

export function identify(distinctId: string, properties?: Properties): void {
  withPostHog((posthog) => posthog.identify(distinctId, properties));
}

/** Attach properties to every subsequent event in this browser (persisted). */
export function register(properties: Properties): void {
  withPostHog((posthog) => posthog.register(properties));
}

/** Drop the identity on logout so the next visitor starts anonymous. */
export function reset(): void {
  withPostHog((posthog) => posthog.reset());
}

export function applyConsent(granted: boolean): void {
  withPostHog((posthog) => {
    if (granted) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  });
}
